import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  BrainIcon,
  SendIcon,
  MessageSquareIcon,
  LoaderIcon,
  MapPinIcon,
  FileQuestionIcon
} from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { aiRuntimeApi } from "@/services/api-v2.service";
import {
  aiAgentsUserApi,
  SIDEBAR_CHAT_AGENT_OPTIONS,
  SPATIAL_MCP_AGENT_CODE,
} from "@/services/ai-agents-user.service";
import { applySpatialMcpMapBridge } from "@/lib/mcp-spatial-map-bridge";
import type { Geometry } from "@/types/api-v2";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Message {
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}

interface AIChatPanelProps {
  selectedGeometry?: Geometry | null;
}

export default function AIChatPanel({ selectedGeometry }: AIChatPanelProps) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [analysisQuestion, setAnalysisQuestion] = useState("");
  /** Роль для обычного диалога (POST /ai/text-generation). */
  const [chatAgentCode, setChatAgentCode] = useState<string>("default_chat");
  /** Роль для анализа геометрии (POST /ai/analyze-geometry). */
  const [geometryAgentCode, setGeometryAgentCode] = useState<string>("object_analyst");

  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { data: aiRuntime } = useQuery({
    queryKey: ["ai", "runtime-availability"],
    queryFn: () => aiRuntimeApi.getAvailability(),
  });

  const hasLlmForChat = aiRuntime?.usable === true;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const generateTextMutation = useMutation({
    mutationFn: async ({ prompt, agent_code }: { prompt: string; agent_code: string }) => {
      return aiAgentsUserApi.textGeneration({ prompt, agent_code });
    },
    onSuccess: (data) => {
      const assistantMessage: Message = {
        content: data.text,
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setInput("");
    },
    onError: (error) => {
      const errorMessage: Message = {
        content: `Ошибка: ${error.message}. Проверьте настройки LLM в админке («ИИ-агенты»).`,
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  const spatialMcpMutation = useMutation({
    mutationFn: async (message: string) => aiAgentsUserApi.spatialMcpChat(message),
    onSuccess: (data) => {
      const bridge = applySpatialMcpMapBridge(data);
      const extras: string[] = [];
      if (bridge.featuresShown > 0) {
        extras.push(`на карте: ${bridge.featuresShown} геом.`);
      }
      if (bridge.cameraApplied) {
        extras.push("камера обновлена");
      }
      const suffix = extras.length > 0 ? `\n\n— ${extras.join(", ")}` : "";
      const assistantMessage: Message = {
        content: `${data.assistant_text || "(пустой ответ агента)"}${suffix}`,
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setInput("");
    },
    onError: (error) => {
      const errorMessage: Message = {
        content: `Ошибка spatial MCP: ${error.message}. Нужны LLM с tool-calling и права MCP.`,
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  const chatPending = generateTextMutation.isPending || spatialMcpMutation.isPending;

  const analyzeGeometryMutation = useMutation({
    mutationFn: async ({
      geometryId,
      question,
      agent_code,
    }: {
      geometryId: number;
      question: string;
      agent_code: string;
    }) => {
      return aiAgentsUserApi.analyzeGeometry({
        geometry_id: geometryId,
        question,
        agent_code,
      });
    },
    onSuccess: (data, variables) => {
      const userMessage: Message = {
        content: `Анализ геометрии: ${variables.question}`,
        role: 'user',
        timestamp: new Date(),
      };
      
      const assistantMessage: Message = {
        content: data.analysis,
        role: 'assistant',
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setShowAnalysisDialog(false);
      setAnalysisQuestion("");
    },
    onError: (error) => {
      const errorMessage: Message = {
        content: `Ошибка анализа геометрии: ${error.message}. Пожалуйста, проверьте настройки интеграции с OpenAI.`,
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setShowAnalysisDialog(false);
    },
  });

  const handleSendMessage = () => {
    if (!input.trim() || !hasLlmForChat || chatPending) return;

    const prompt = input.trim();
    const userMessage: Message = {
      content: prompt,
      role: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    if (chatAgentCode === SPATIAL_MCP_AGENT_CODE) {
      spatialMcpMutation.mutate(prompt);
      return;
    }
    generateTextMutation.mutate({ prompt, agent_code: chatAgentCode });
  };

  const handleOpenAnalysisDialog = () => {
    if (!selectedGeometry || !hasLlmForChat) return;
    setShowAnalysisDialog(true);
  };

  const handleAnalyzeGeometry = () => {
    if (!selectedGeometry || !analysisQuestion.trim() || !hasLlmForChat) return;
    
    analyzeGeometryMutation.mutate({
      geometryId: selectedGeometry.id,
      question: analysisQuestion,
      agent_code: geometryAgentCode,
    });
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-sidebar text-sidebar-foreground">
      <div className="flex flex-col gap-2 px-4 py-3 border-b border-sidebar-border bg-sidebar sticky top-0 z-10">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <BrainIcon className="h-5 w-5 text-primary shrink-0" />
            <h2 className="text-lg font-semibold truncate text-sidebar-foreground">Ассистент ИИ</h2>
          </div>
          {selectedGeometry && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleOpenAnalysisDialog}
            className="flex items-center gap-1"
          >
            <MapPinIcon className="h-4 w-4" />
            Анализ геометрии
          </Button>
          )}
        </div>
        {hasLlmForChat && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Роль для текстового чата</span>
            <Select value={chatAgentCode} onValueChange={setChatAgentCode}>
              <SelectTrigger className="h-8 text-xs z-[250] bg-background border-border text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                {SIDEBAR_CHAT_AGENT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} title={o.hint}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground leading-snug">
              {SIDEBAR_CHAT_AGENT_OPTIONS.find((o) => o.value === chatAgentCode)?.hint}
            </p>
          </div>
        )}
      </div>
      
      <div className="flex-1 overflow-auto p-4 pb-24 space-y-4">
        {!hasLlmForChat ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center max-w-md p-6 rounded-lg border border-sidebar-border bg-sidebar-accent/40">
              <BrainIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2 text-sidebar-foreground">LLM не настроен</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Администратору: добавьте активного провайдера с API-ключом в разделе «ИИ-агенты»
                (таблица <code className="text-xs">ai.llm_providers</code>).
              </p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center max-w-md p-6">
              <MessageSquareIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2 text-sidebar-foreground">Начните диалог с ассистентом</h3>
              <p className="text-sm text-muted-foreground">
                Задайте вопрос или воспользуйтесь анализом геометрии для получения информации.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, i) => (
              <div
                key={i}
                className={`flex ${
                  message.role === "assistant" ? "justify-start" : "justify-end"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 border ${
                    message.role === "assistant"
                      ? "bg-sidebar-accent/80 border-sidebar-border text-sidebar-foreground"
                      : "bg-primary text-primary-foreground border-transparent"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{message.content}</div>
                  <div className={`text-xs mt-1 ${
                    message.role === "assistant"
                      ? "text-muted-foreground"
                      : "text-primary-foreground/80"
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      
      <div className="p-3 border-t border-sidebar-border bg-sidebar sticky bottom-0 z-10">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Введите ваш вопрос..."
              className="resize-none min-h-[60px] max-h-[80px] overflow-auto bg-background text-foreground border-border"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={!hasLlmForChat || chatPending}
            />
          </div>
          <Button 
            onClick={handleSendMessage} 
            size="icon"
            disabled={!input.trim() || !hasLlmForChat || chatPending}
            className="h-10 w-10"
          >
            {chatPending ? (
              <LoaderIcon className="h-5 w-5 animate-spin" />
            ) : (
              <SendIcon className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>
      
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Анализ геометрии</DialogTitle>
            <DialogDescription>
              Задайте вопрос по выбранной геометрии для анализа с помощью ИИ
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Роль для анализа контура</Label>
              <Select value={geometryAgentCode} onValueChange={setGeometryAgentCode}>
                <SelectTrigger className="z-[250]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="object_analyst">Аналитик объектов</SelectItem>
                  <SelectItem value="geospatial_mapper">Картограф (акцент на геометрию и карту)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-3">
              <FileQuestionIcon className="h-5 w-5 mt-1 text-muted-foreground" />
              <div className="flex-1">
                <Label htmlFor="analysisQuestion" className="mb-2 block">
                  Вопрос для анализа
                </Label>
                <Textarea
                  id="analysisQuestion"
                  placeholder="Например: Какой тип объекта изображен? Какие особенности геометрии видны?"
                  value={analysisQuestion}
                  onChange={(e) => setAnalysisQuestion(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalysisDialog(false)}>
              Отмена
            </Button>
            <Button 
              onClick={handleAnalyzeGeometry} 
              disabled={!analysisQuestion.trim() || analyzeGeometryMutation.isPending}
            >
              {analyzeGeometryMutation.isPending ? (
                <>
                  <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                  Анализ...
                </>
              ) : (
                "Анализировать"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}